/* eslint-disable @typescript-eslint/no-this-alias, @typescript-eslint/no-explicit-any */
import L from "leaflet";
import "@libs/heatmap.js";

// Declare the heatmap.js global
declare global {
  interface Window {
    h337: {
      create: (config: HeatmapConfig) => HeatmapInstance;
    };
  }
}

interface HeatmapConfig {
  container?: HTMLElement;
  radius?: number;
  maxOpacity?: number;
  scaleRadius?: boolean;
  useLocalExtrema?: boolean;
  latField?: string;
  lngField?: string;
  valueField?: string;
}

interface HeatmapInstance {
  setData: (data: HeatmapGeneratedData) => void;
  _renderer: {
    setDimensions: (width: number, height: number) => void;
  };
}

interface HeatmapDataPoint {
  lat?: number;
  lng?: number;
  [key: string]: unknown;
}

interface HeatmapData {
  max?: number;
  min?: number;
  data: HeatmapDataPoint[];
}

interface HeatmapGeneratedData {
  max: number;
  min: number;
  data: Array<{
    x: number;
    y: number;
    radius: number;
    [key: string]: unknown;
  }>;
}

interface InternalDataPoint {
  latlng: L.LatLng;
  radius?: number;
  [key: string]: unknown;
}

// CSS_TRANSFORM detection
const CSS_TRANSFORM = (function (): string {
  const div = document.createElement("div");
  const props = [
    "transform",
    "WebkitTransform",
    "MozTransform",
    "OTransform",
    "msTransform",
  ];

  for (let i = 0; i < props.length; i++) {
    const prop = props[i];
    if ((div.style as any)[prop] !== undefined) {
      return prop;
    }
  }
  return props[0];
})();

// code is copied at leaflet-heatmap library
const HeatmapOverlay = (L.Layer as any).extend({
  initialize: function (config: HeatmapConfig) {
    this.cfg = config;
    this._el = L.DomUtil.create("div", "leaflet-zoom-hide");
    this._data = [] as InternalDataPoint[];
    this._max = 1;
    this._min = 0;
    this.cfg.container = this._el;
  },

  onAdd: function (map: L.Map) {
    const size = map.getSize();

    this._map = map;

    this._width = size.x;
    this._height = size.y;

    this._el.style.width = size.x + "px";
    this._el.style.height = size.y + "px";
    this._el.style.position = "absolute";

    this._origin = this._map.layerPointToLatLng(new L.Point(0, 0));

    map.getPanes().overlayPane.appendChild(this._el);

    if (!this._heatmap) {
      this._heatmap = window.h337.create(this.cfg);
    }

    // this resets the origin and redraws whenever
    // the zoom changed or the map has been moved
    map.on("moveend", this._reset, this);
    this._draw();
  },

  addTo: function (map: L.Map): L.Layer {
    map.addLayer(this);
    return this;
  },

  onRemove: function (map: L.Map) {
    // remove layer's DOM elements and listeners
    map.getPanes().overlayPane.removeChild(this._el);

    map.off("moveend", this._reset, this);
  },

  _draw: function () {
    if (!this._map) {
      return;
    }

    const mapPane = this._map.getPanes().mapPane;
    const point = (mapPane as any)._leaflet_pos;

    // reposition the layer
    (this._el.style as any)[CSS_TRANSFORM] =
      "translate(" +
      -Math.round(point.x) +
      "px," +
      -Math.round(point.y) +
      "px)";

    this._update();
  },

  _update: function () {
    const generatedData: HeatmapGeneratedData = {
      max: this._max,
      min: this._min,
      data: [],
    };

    const bounds = this._map.getBounds();
    const zoom = this._map.getZoom();
    const scale = Math.pow(2, zoom);

    if (this._data.length === 0) {
      if (this._heatmap) {
        this._heatmap.setData(generatedData);
      }
      return;
    }

    const latLngPoints: Array<{
      x: number;
      y: number;
      radius: number;
      [key: string]: unknown;
    }> = [];
    const radiusMultiplier = this.cfg.scaleRadius ? scale : 1;
    let localMax = 0;
    let localMin = 0;
    const valueField = this.cfg.valueField || "value";
    let len = this._data.length;

    while (len--) {
      const entry = this._data[len];
      const value = entry[valueField] as number;
      const latlng = entry.latlng;

      // we don't wanna render points that are not even on the map ;-)
      if (!bounds.contains(latlng)) {
        continue;
      }
      // local max is the maximum within current bounds
      localMax = Math.max(value, localMax);
      localMin = Math.min(value, localMin);

      const point = this._map.latLngToContainerPoint(latlng);
      const latlngPoint: Record<string, unknown> = {
        x: Math.round(point.x),
        y: Math.round(point.y),
      };
      latlngPoint[valueField] = value;

      let radius: number;

      if (entry.radius) {
        radius = entry.radius * radiusMultiplier;
      } else {
        radius = (this.cfg.radius || 2) * radiusMultiplier;
      }
      latlngPoint.radius = radius;
      latLngPoints.push(latlngPoint as {
        x: number;
        y: number;
        radius: number;
        [key: string]: unknown;
      });
    }
    if (this.cfg.useLocalExtrema) {
      generatedData.max = localMax;
      generatedData.min = localMin;
    }

    generatedData.data = latLngPoints;

    this._heatmap.setData(generatedData);
  },

  setData: function (data: HeatmapData) {
    this._max = data.max || this._max;
    this._min = data.min || this._min;
    const latField = this.cfg.latField || "lat";
    const lngField = this.cfg.lngField || "lng";
    const valueField = this.cfg.valueField || "value";

    // transform data to latlngs
    const dataArr = data.data;
    let len = dataArr.length;
    const d: InternalDataPoint[] = [];

    while (len--) {
      const entry = dataArr[len];
      const latlng = new L.LatLng(
        entry[latField] as number,
        entry[lngField] as number
      );
      const dataObj: InternalDataPoint = { latlng: latlng };
      dataObj[valueField] = entry[valueField];
      if ((entry as Record<string, unknown>).radius) {
        dataObj.radius = (entry as Record<string, unknown>).radius as number;
      }
      d.push(dataObj);
    }
    this._data = d;

    this._draw();
  },

  // experimential... not ready.
  addData: function (pointOrArray: HeatmapDataPoint | HeatmapDataPoint[]) {
    if (Array.isArray(pointOrArray) && pointOrArray.length > 0) {
      let len = pointOrArray.length;
      while (len--) {
        this.addData(pointOrArray[len]);
      }
    } else {
      const latField = this.cfg.latField || "lat";
      const lngField = this.cfg.lngField || "lng";
      const valueField = this.cfg.valueField || "value";
      const entry = pointOrArray as HeatmapDataPoint;
      const latlng = new L.LatLng(
        entry[latField] as number,
        entry[lngField] as number
      );
      const dataObj: InternalDataPoint = { latlng: latlng };

      dataObj[valueField] = entry[valueField];
      this._max = Math.max(this._max, dataObj[valueField] as number);
      this._min = Math.min(this._min, dataObj[valueField] as number);

      if ((entry as Record<string, unknown>).radius) {
        dataObj.radius = (entry as Record<string, unknown>).radius as number;
      }
      this._data.push(dataObj);
      this._draw();
    }
  },

  _reset: function () {
    this._origin = this._map.layerPointToLatLng(new L.Point(0, 0));

    const size = this._map.getSize();
    if (this._width !== size.x || this._height !== size.y) {
      this._width = size.x;
      this._height = size.y;

      this._el.style.width = this._width + "px";
      this._el.style.height = this._height + "px";

      this._heatmap._renderer.setDimensions(this._width, this._height);
    }
    this._draw();
  },
});

const HeatMapLayerClass = HeatmapOverlay.extend({
  options: {
    // radius should be small ONLY if scaleRadius is true (or small radius is intended)
    // if scaleRadius is false it will be the constant radius used in pixels
    radius: 2,
    maxOpacity: 0.8,
    // scales the radius based on map zoom
    scaleRadius: true,
    // if set to false the heatmap uses the global maximum for colorization
    // if activated: uses the data maximum within the current map boundaries
    //   (there will always be a red spot with useLocalExtremas true)
    useLocalExtrema: true,
    // which field name in your data represents the latitude - default "lat"
    latField: "lat",
    // which field name in your data represents the longitude - default "lng"
    lngField: "lng",
    // which field name in your data represents the data value - default "value"
    valueField: "count",
  },

  updateData: function (data: HeatmapData) {
    const self = this;
    self.setData(data);
  },
});

(L as any).HeatMapLayer = HeatMapLayerClass;
(L as any).heatMapLayer = function (options?: HeatmapConfig): any {
  return new HeatMapLayerClass(options);
};

export const HeatMapLayer = (L as any).heatMapLayer;
